import { useMutation } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { updateCampaign } from 'api/campaigns';
import * as CampaignDescriptionForm from 'assets/js/pages/campaign-settings/campaign-details/CampaignDescriptionForm';
import { CampaignDescriptionValues } from 'assets/js/pages/campaign-settings/campaign-details/types';
import * as Form from 'components/form';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { queryClient } from 'queries/queryClient';
import { GivebutterAxiosError } from 'types/error';

const CampaignStoryOrDescription = () => {
    const toast = useToast();
    const { data: campaign } = useCurrentCampaign();
    const isDescription = campaign.type === 'collect';
    const contentType = isDescription ? 'description' : 'story';

    const methods = Form.useGivebutterForm<CampaignDescriptionValues>({
        defaultValues: {
            description: campaign.description ?? '',
        },
    });

    const {
        formState: { isDirty },
        handleSubmit,
        reset,
    } = methods;

    const { mutate: submit, isPending: isLoading } = useMutation({
        mutationFn: (data: CampaignDescriptionValues) =>
            updateCampaign({ campaignId: campaign.id, data }),
        onSuccess: data => {
            toast.success(`Updated the campaign ${contentType}!`);
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.campaign(campaign.id),
            });
            reset({ description: data.description });
        },
        onError: (e: GivebutterAxiosError) =>
            toast.error(
                e.response?.data?.message ?? `Failed to update the campaign ${contentType}`,
            ),
    });

    return (
        <>
            <Form.Provider {...methods}>
                <form onSubmit={handleSubmit(values => submit(values))}>
                    <CampaignDescriptionForm.Root>
                        <CampaignDescriptionForm.Header>
                            <CampaignDescriptionForm.Container>
                                <CampaignDescriptionForm.Title />
                                <CampaignDescriptionForm.Description />
                            </CampaignDescriptionForm.Container>
                            <CampaignDescriptionForm.Submit
                                isSubmitting={isLoading}
                                disabled={!isDirty}
                            />
                        </CampaignDescriptionForm.Header>
                        <CampaignDescriptionForm.Editor />
                    </CampaignDescriptionForm.Root>
                </form>
            </Form.Provider>
        </>
    );
};

export { CampaignStoryOrDescription };
